import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  addQmCommentRequest,
  confirmAttachmentRequest,
  getQmDashboardSummaryRequest,
  getQmRecordRequest,
  getUploadUrlRequest,
  listQmSiteInboundCoordinatorSitesRequest,
  listQmSiteInboundSitesRequest,
  listAttachmentsRequest,
  listQmRecordsRequest,
  listQmReferenceClosureCodesRequest,
  QmAttachment,
  listQmReferenceLocationsRequest,
  listQmReferenceReasonsRequest,
  listQmSiteInboundLocationsRequest,
  ListQmRecordsFilterInput,
  QmCrudStatus,
  QmRecord,
  QmSiteInboundCoordinatorSiteOption,
  QmSiteInboundSiteOption,
  subscribeQmLiveUpdates,
  uploadFileToS3,
  updateQmRecordRequest
} from '../../api/qmCrud';
import { printQm } from '../../common/helperFunctions/print';
import { captureError } from '../../common/sentry';
import { compressImageForUpload } from '../../common/imageCompression';
import { getQmListRoleLabel, toQmListRoleValue } from '../../common/qmRoles';
import { getQmTypeLabel } from '../../common/qmTypes';
import { StyledButton } from '../../components';
import { getNormalizedUserEmail } from '../../common/userIdentity';
import { useAlert, useCurrentUser, useDatastore, useQmFeatureFlags } from '../../hooks';
import { useUserDetails } from '../../contexts/UserDetailsContext';
import QmDetailDialog from './QmDetailDialog';
import type { QmDetailDialogModel } from './QmDetailDialog';
import { QMPrintProps } from '../../../../Common/Types/types';
import { useLocation, useNavigate } from 'react-router-dom';

const STATUS_OPTIONS: QmCrudStatus[] = ['CREATED', 'PENDING', 'CLOSED'];
const PAGE_SIZE = 25;
const SITE_INBOUND_WAREHOUSE_COORDINATOR_REASON_CODES = new Set(['SITE_INBOUND_CYCLE_COUNT_IN_PROGRESS']);
const SITE_INBOUND_ATTACHMENT_OPTIONAL_REASON_CODES = new Set([
  'SITE_INBOUND_SSCC_NOT_DELIVERED',
  'SITE_INBOUND_ASN_NOT_DELIVERED'
]);
const ATTACHMENT_ADDED_PREFIX = 'Attachment added:';

function getQmSearchText(record: QmRecord): string {
  const poLineValues = (record.poLines || []).flatMap((line) => {
    const payload =
      line.linePayload && typeof line.linePayload === 'object' && !Array.isArray(line.linePayload)
        ? (line.linePayload as Record<string, unknown>)
        : {};

    return [
      line.poLineId,
      line.poLine,
      line.itemId,
      payload.asn,
      payload.sourcePartId,
      payload.description,
      payload.receivingWarehouseId,
      line.orderedQuantity,
      line.receivedQuantity,
      line.quantityVariance
    ];
  });

  const packageValues = (record.packages || []).flatMap((pkg) => [pkg.packageNumber, pkg.packageType]);

  return [
    record.qmNumber,
    record.type,
    record.typeLabel,
    record.status,
    record.reasonCode,
    record.reasonLabel,
    record.closureCode,
    record.closureLabel,
    record.sscc,
    record.asn,
    record.tripId,
    record.tripReference,
    record.manifestId,
    record.poNumber,
    record.poLine,
    record.poLineId,
    record.itemId,
    record.supplierCode,
    record.supplierName,
    record.contactName,
    record.itemLocation,
    record.itemLocationId,
    record.receivingWarehouseId,
    record.packageCount,
    record.packageType,
    record.otherInformation,
    record.description,
    record.closureNotes,
    record.createdBy,
    record.updatedBy,
    ...(record.comments || []).map((entry) => entry.body),
    ...(record.changeHistory || []).flatMap((entry) => [
      entry.fieldName,
      entry.oldValue,
      entry.newValue,
      entry.changedBy
    ]),
    ...poLineValues,
    ...packageValues
  ]
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
    .join(' ')
    .toLowerCase();
}

function renderQmActionCellContent(value: string | null | undefined) {
  const actionText = value?.trim() || '-';
  const attachmentMatch = actionText.match(/^Attachment added:\s*(.*)$/i);

  if (attachmentMatch) {
    const fileName = attachmentMatch[1]?.trim() || '-';
    return (
      <Box
        title={actionText}
        sx={{
          minWidth: 0,
          maxWidth: '100%',
          fontSize: '0.78rem',
          lineHeight: 1.35
        }}>
        <Typography component="div" sx={{ fontSize: '0.78rem', lineHeight: 1.25 }}>
          {ATTACHMENT_ADDED_PREFIX}
        </Typography>
        <Typography
          component="div"
          sx={{
            display: 'block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.78rem',
            lineHeight: 1.35
          }}>
          {fileName}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      sx={{
        fontSize: '0.78rem',
        lineHeight: 1.35,
        whiteSpace: 'normal',
        wordBreak: 'break-word'
      }}>
      {actionText}
    </Typography>
  );
}

type ReprintMode = 'ALL' | 'ONE';

type DashboardFilters = {
  includeInbound: boolean;
  includeSiteInbound: boolean;
  reasonCode: string;
  closureCode: string;
  createdBy: string;
  itemLocation: string;
  siteInboundSiteId: string;
  siteInboundLocationId: string;
  qmNumber: string;
  poNumber: string;
  createdFrom: string;
  createdTo: string;
  resolvedFrom: string;
  resolvedTo: string;
  search: string;
};

const EMPTY_DASHBOARD_FILTERS: DashboardFilters = {
  includeInbound: true,
  includeSiteInbound: true,
  reasonCode: '',
  closureCode: '',
  createdBy: '',
  itemLocation: '',
  siteInboundSiteId: '',
  siteInboundLocationId: '',
  qmNumber: '',
  poNumber: '',
  createdFrom: '',
  createdTo: '',
  resolvedFrom: '',
  resolvedTo: '',
  search: ''
};

function formatStatusLabel(status: QmCrudStatus | string | null | undefined): string {
  if (!status) return 'N/A';
  if (status === 'CREATED') return 'Open';
  if (status === 'PENDING') return 'Pending';
  if (status === 'CLOSED') return 'Closed';
  return status;
}

function getStatusChipSx(status: QmCrudStatus | string | null | undefined) {
  if (status === 'CREATED') {
    return {
      bgcolor: '#dcfce7',
      color: '#166534',
      border: '1px solid #86efac',
      fontWeight: 700,
      '& .MuiChip-label': {
        fontSize: '0.74rem',
        fontWeight: 700,
        letterSpacing: 0.15
      }
    };
  }

  if (status === 'PENDING') {
    return {
      bgcolor: '#fffbeb',
      color: '#92400e',
      border: '1px solid #fde68a',
      fontWeight: 700,
      '& .MuiChip-label': {
        fontSize: '0.74rem',
        fontWeight: 700,
        letterSpacing: 0.15
      }
    };
  }

  if (status === 'CLOSED') {
    return {
      bgcolor: '#e5e7eb',
      color: '#374151',
      border: '1px solid #9ca3af',
      fontWeight: 700,
      '& .MuiChip-label': {
        fontSize: '0.74rem',
        fontWeight: 700,
        letterSpacing: 0.15
      }
    };
  }

  return {
    bgcolor: '#f8fafc',
    color: '#334155',
    border: '1px solid #cbd5e1',
    fontWeight: 700,
    '& .MuiChip-label': {
      fontSize: '0.74rem',
      fontWeight: 700,
      letterSpacing: 0.15
    }
  };
}

function normalizeSearchInput(value: string): string {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildLabelFilterOptions(options: Array<{ code: string; label: string }>) {
  const byLabel = new Map<string, { code: string; label: string }>();

  options.forEach((option) => {
    const label = normalizeSearchInput(option.label || option.code);
    const key = label.toLowerCase();
    if (label && !byLabel.has(key)) {
      byLabel.set(key, { code: label, label });
    }
  });

  return Array.from(byLabel.values());
}

function DateRangeFilter({
  label,
  from,
  to,
  applied,
  onFromChange,
  onToChange
}: {
  label: string;
  from: string;
  to: string;
  applied: boolean;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const fromLabel = from ? dayjs(from).format('DD/MM/YYYY') : '';
  const toLabel = to ? dayjs(to).format('DD/MM/YYYY') : '';
  const displayValue =
    fromLabel && toLabel
      ? `${fromLabel} - ${toLabel}`
      : fromLabel
        ? `From ${fromLabel}`
        : toLabel
          ? `To ${toLabel}`
          : '';

  return (
    <>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={displayValue}
        placeholder="Select range"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          minWidth: { md: 250 },
          '& .MuiOutlinedInput-root': {
            bgcolor: applied ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: applied ? 'primary.main' : 'divider'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: applied ? 'primary.main' : 'text.secondary'
            }
          },
          '& .MuiInputLabel-root': {
            color: applied ? 'primary.main' : 'text.secondary'
          }
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Stack spacing={1.25} sx={{ p: 1.5, width: 320 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{label} Range</Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={from}
              onChange={(event) => onFromChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={to}
              inputProps={{ min: from || undefined }}
              onChange={(event) => onToChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <StyledButton
              size="small"
              variant="outlined"
              onClick={() => {
                onFromChange('');
                onToChange('');
              }}>
              Clear
            </StyledButton>
            <StyledButton size="small" variant="contained" onClick={() => setAnchorEl(null)}>
              Done
            </StyledButton>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

export default function QmCoordinatorDashboard() {
  const { qmNewDashboardEnabled: isQmNewDashboardEnabled, isLoading: isQmFeatureFlagsLoading } = useQmFeatureFlags();

  const alert = useAlert();
  const user = useCurrentUser();
  const store = useDatastore();
  const { userDetails } = useUserDetails();
  const routeLocation = useLocation();
  const navigate = useNavigate();

  const qmListRole = toQmListRoleValue(userDetails?.qmListRole);
  const isNoRoleAccess = qmListRole === 'QM_LIST_OWN_ONLY';
  const canEditAll = qmListRole === 'QM_LIST_EDIT_ALL';

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<QmRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  const [summary, setSummary] = useState({ total: 0, created: 0, pending: 0, closed: 0 });
  const [overallSummary, setOverallSummary] = useState({ total: 0, created: 0, pending: 0, closed: 0 });

  const [statusQuickFilter, setStatusQuickFilter] = useState<QmCrudStatus | ''>('');
  const [includeInboundFilter, setIncludeInboundFilter] = useState(true);
  const [includeSiteInboundFilter, setIncludeSiteInboundFilter] = useState(true);
  const [reasonFilter, setReasonFilter] = useState('');
  const [closureFilter, setClosureFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [siteInboundSiteFilter, setSiteInboundSiteFilter] = useState('');
  const [siteInboundLocationFilter, setSiteInboundLocationFilter] = useState('');
  const [qmNumberFilter, setQmNumberFilter] = useState('');
  const [poNumberFilter, setPoNumberFilter] = useState('');
  const [createdFromFilter, setCreatedFromFilter] = useState('');
  const [createdToFilter, setCreatedToFilter] = useState('');
  const [resolvedFromFilter, setResolvedFromFilter] = useState('');
  const [resolvedToFilter, setResolvedToFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<DashboardFilters>(EMPTY_DASHBOARD_FILTERS);

  const [reasonOptions, setReasonOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [reasonFilterOptions, setReasonFilterOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [editReasonOptions, setEditReasonOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [closureOptions, setClosureOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [closureFilterOptions, setClosureFilterOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [locationOptions, setLocationOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [siteInboundLocationFilterOptions, setSiteInboundLocationFilterOptions] = useState<
    Array<{ code: string; label: string }>
  >([]);
  const [detailLocationOptions, setDetailLocationOptions] = useState<Array<{ code: string; label: string }>>([]);
  const [siteInboundSites, setSiteInboundSites] = useState<QmSiteInboundSiteOption[]>([]);
  const [siteInboundCoordinatorSiteOptions, setSiteInboundCoordinatorSiteOptions] = useState<
    QmSiteInboundCoordinatorSiteOption[]
  >([]);

  const [selectedQm, setSelectedQm] = useState<QmRecord | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [attachments, setAttachments] = useState<QmAttachment[]>([]);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState<{ name: string; url: string } | null>(null);

  const [editStatus, setEditStatus] = useState<QmCrudStatus>('PENDING');
  const [editReasonCode, setEditReasonCode] = useState('');
  const [editSupplierName, setEditSupplierName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editSiteInboundSiteId, setEditSiteInboundSiteId] = useState('');
  const [editItemLocation, setEditItemLocation] = useState('');
  const [editOtherInformation, setEditOtherInformation] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editClosureCode, setEditClosureCode] = useState('');
  const [editClosureNotes, setEditClosureNotes] = useState('');
  const [actionComment, setActionComment] = useState('');
  const [isAddingActionComment, setIsAddingActionComment] = useState(false);
  const [isReprintingLabels, setIsReprintingLabels] = useState(false);
  const [isReprintDialogOpen, setIsReprintDialogOpen] = useState(false);
  const [isSiteRolesDialogOpen, setIsSiteRolesDialogOpen] = useState(false);
  const [reprintMode, setReprintMode] = useState<ReprintMode>('ALL');
  const [reprintLabelNumber, setReprintLabelNumber] = useState('1');
  const alertRef = useRef(alert);
  const selectedQmIdRef = useRef<number | null>(null);
  const isDetailDialogOpenRef = useRef(false);
  const dashboardCountRefreshTimerRef = useRef<number | null>(null);
  const openedQmFromUrlRef = useRef<string | null>(null);

  useEffect(() => {
    alertRef.current = alert;
  }, [alert]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageRangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageRangeEnd = totalCount === 0 ? 0 : Math.min(page * PAGE_SIZE, totalCount);
  const sectionCardSx = {
    p: 1.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper'
  } as const;

  const getAppliedFilterFieldSx = (isApplied: boolean) => ({
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isApplied ? 'primary.main' : 'divider'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isApplied ? 'primary.main' : 'text.secondary'
      }
    },
    '& .MuiInputLabel-root': {
      color: isApplied ? 'primary.main' : 'text.secondary'
    }
  });

  const activeAppliedFilterCount = useMemo(() => {
    const typeFilterCount = appliedFilters.includeInbound && appliedFilters.includeSiteInbound ? 0 : 1;
    const appliedFieldCount = [
      appliedFilters.reasonCode,
      appliedFilters.closureCode,
      appliedFilters.createdBy,
      appliedFilters.itemLocation,
      appliedFilters.siteInboundSiteId,
      appliedFilters.siteInboundLocationId,
      appliedFilters.qmNumber,
      appliedFilters.poNumber,
      appliedFilters.createdFrom,
      appliedFilters.createdTo,
      appliedFilters.resolvedFrom,
      appliedFilters.resolvedTo,
      appliedFilters.search
    ].filter((value) => String(value || '').trim() !== '').length;
    const statusFilterCount = statusQuickFilter ? 1 : 0;
    return typeFilterCount + appliedFieldCount + statusFilterCount;
  }, [appliedFilters, statusQuickFilter]);

  const showFilteredOutNotice = useMemo(() => {
    if (!selectedQm) return false;
    return !rows.some((item) => item.qmId === selectedQm.qmId);
  }, [rows, selectedQm]);

  const getLocationLabel = useCallback(
    (itemLocation: string | null | undefined) => {
      const value = (itemLocation || '').trim();
      if (!value) return '-';

      const normalized = value.toLowerCase();
      const matched = locationOptions.find(
        (location) =>
          location.code.trim().toLowerCase() === normalized || location.label.trim().toLowerCase() === normalized
      );

      return matched?.label || `${value} (archived)`;
    },
    [locationOptions]
  );

  const getQmLocationLabel = useCallback(
    (qm: QmRecord | null | undefined) => {
      if (!qm) return '-';
      if (qm.type === 'SITE_INBOUND' && qm.itemLocationId) {
        return String(qm.itemLocation || '').trim() || '-';
      }
      return getLocationLabel(qm.itemLocation);
    },
    [getLocationLabel]
  );

  const normalizedUserEmail = useMemo(() => getNormalizedUserEmail(user.email), [user.email]);
  const siteInboundCoordinatorSiteIds = useMemo(() => {
    if (!normalizedUserEmail) return new Set<string>();

    return new Set(
      siteInboundCoordinatorSiteOptions
        .filter((site) =>
          (site.qmCoordinatorEmails || []).some((email) => getNormalizedUserEmail(email) === normalizedUserEmail)
        )
        .map((site) => String(site.siteId || '').trim())
        .filter((siteId) => siteId.length > 0)
    );
  }, [normalizedUserEmail, siteInboundCoordinatorSiteOptions]);
  const siteInboundCoordinatorSites = useMemo(() => {
    if (!normalizedUserEmail) return [];

    return siteInboundCoordinatorSiteOptions
      .filter((site) =>
        (site.qmCoordinatorEmails || []).some((email) => getNormalizedUserEmail(email) === normalizedUserEmail)
      )
      .map((site) => {
        const label = String(site.label || '').trim();
        const siteId = String(site.siteId || '').trim();
        const displayLabel = label || (siteId ? `Site ${siteId}` : '');
        return {
          id: siteId,
          label: displayLabel,
          display: siteId && displayLabel ? `${displayLabel} (${siteId})` : displayLabel || siteId
        };
      })
      .filter((site) => site.display.length > 0);
  }, [normalizedUserEmail, siteInboundCoordinatorSiteOptions]);
  const siteInboundCoordinatorReasonLabels = useMemo(
    () =>
      Array.from(SITE_INBOUND_WAREHOUSE_COORDINATOR_REASON_CODES).map((reasonCode) => {
        const reason = reasonOptions.find((option) => option.code === reasonCode);
        return {
          code: reasonCode,
          label:
            reason?.label ||
            reasonCode
              .replace(/^SITE_INBOUND_/, '')
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b\w/g, (char) => char.toUpperCase())
        };
      }),
    [reasonOptions]
  );
  const siteRoleSummary =
    siteInboundCoordinatorSites.length === 1
      ? '1 Site Specific Coordinator Role'
      : `${siteInboundCoordinatorSites.length} Site Specific Coordinator Roles`;

  const canCoordinateSelectedQm = useMemo(() => {
    if (!selectedQm) return false;
    if (canEditAll) return true;
    if (!normalizedUserEmail) return false;

    return Boolean(
      selectedQm.type === 'SITE_INBOUND' &&
      SITE_INBOUND_WAREHOUSE_COORDINATOR_REASON_CODES.has(selectedQm.reasonCode) &&
      selectedQm.receivingWarehouseId &&
      siteInboundCoordinatorSiteIds.has(String(selectedQm.receivingWarehouseId))
    );
  }, [canEditAll, normalizedUserEmail, selectedQm, siteInboundCoordinatorSiteIds]);
  const selectedQmCoordinatorSite = useMemo(() => {
    const receivingWarehouseId = String(selectedQm?.receivingWarehouseId || '').trim();
    if (
      !selectedQm ||
      selectedQm.type !== 'SITE_INBOUND' ||
      !SITE_INBOUND_WAREHOUSE_COORDINATOR_REASON_CODES.has(selectedQm.reasonCode) ||
      !receivingWarehouseId
    ) {
      return null;
    }

    return siteInboundCoordinatorSites.find((site) => site.id === receivingWarehouseId) || null;
  }, [selectedQm, siteInboundCoordinatorSites]);

  const canEditSelectedQm = useMemo(() => {
    if (!selectedQm) return false;
    if (canCoordinateSelectedQm) return true;
    if (!normalizedUserEmail) return false;

    return getNormalizedUserEmail(selectedQm.createdBy) === normalizedUserEmail;
  }, [canCoordinateSelectedQm, normalizedUserEmail, selectedQm]);

  useEffect(() => {
    let active = true;

    async function loadDetailLocationOptions() {
      if (!selectedQm || selectedQm.type !== 'SITE_INBOUND') {
        setDetailLocationOptions(locationOptions);
        return;
      }

      const receivingWarehouseId = String(editSiteInboundSiteId || selectedQm.receivingWarehouseId || '').trim();
      if (!receivingWarehouseId) {
        setDetailLocationOptions([]);
        return;
      }

      try {
        const locations = await listQmSiteInboundLocationsRequest(receivingWarehouseId);
        if (!active) return;

        setDetailLocationOptions(
          locations.map((location) => {
            const warehouseLabel = String(location.receivingWarehouseLabel || '').trim();
            return {
              code: String(location.id),
              label: warehouseLabel ? `${warehouseLabel} - ${location.label}` : location.label
            };
          })
        );
      } catch (error) {
        captureError(error, 'qm-coordinator-loadDetailSiteInboundLocations', {
          extra: { qmId: selectedQm.qmId, receivingWarehouseId }
        });
        if (active) {
          setDetailLocationOptions([]);
        }
      }
    }

    void loadDetailLocationOptions();

    return () => {
      active = false;
    };
  }, [editSiteInboundSiteId, locationOptions, selectedQm]);

  const loadStaticOptions = useCallback(async () => {
    try {
      const [inboundReasons, siteInboundReasons] = await Promise.all([
        listQmReferenceReasonsRequest('INBOUND'),
        listQmReferenceReasonsRequest('SITE_INBOUND')
      ]);

      const mergedReasons = [...inboundReasons, ...siteInboundReasons];
      const uniqueReasonOptions = Array.from(
        new Map(mergedReasons.map((item) => [item.code, { code: item.code, label: item.label }])).values()
      );

      setReasonOptions(uniqueReasonOptions);
      setReasonFilterOptions(buildLabelFilterOptions(uniqueReasonOptions));
      setEditReasonOptions(uniqueReasonOptions);
    } catch (error) {
      captureError(error, 'qm-coordinator-loadStaticOptions-reasons');
      alertRef.current('Unable to load QM reason options', 'warning');
    }

    try {
      const locations = await listQmReferenceLocationsRequest();
      setLocationOptions(locations.map((item) => ({ code: item.code, label: item.label })));
    } catch (error) {
      captureError(error, 'qm-coordinator-loadStaticOptions-locations');
      alertRef.current('Unable to load QM location options', 'warning');
    }

    try {
      const sites = await listQmSiteInboundSitesRequest();
      setSiteInboundSites(sites);
    } catch (error) {
      captureError(error, 'qm-coordinator-loadStaticOptions-siteInboundSites');
      alertRef.current('Unable to load Site Inbound site options', 'warning');
    }

    try {
      const coordinatorSites = await listQmSiteInboundCoordinatorSitesRequest();
      setSiteInboundCoordinatorSiteOptions(coordinatorSites);
    } catch (error) {
      captureError(error, 'qm-coordinator-loadStaticOptions-siteInboundCoordinatorSites');
      alertRef.current('Unable to load Site Inbound site coordinator options', 'warning');
    }

    try {
      const closureCodes = await listQmReferenceClosureCodesRequest();
      setClosureFilterOptions(
        buildLabelFilterOptions(closureCodes.map((item) => ({ code: item.code, label: item.label })))
      );
    } catch (error) {
      captureError(error, 'qm-coordinator-loadStaticOptions-closureCodes');
      alertRef.current('Unable to load QM closure options', 'warning');
    }
  }, []);

  useEffect(() => {
    let active = true;

    async function loadSiteInboundLocationFilterOptions() {
      if (!includeSiteInboundFilter || !siteInboundSiteFilter) {
        setSiteInboundLocationFilterOptions([]);
        setSiteInboundLocationFilter('');
        return;
      }

      try {
        const locations = await listQmSiteInboundLocationsRequest(siteInboundSiteFilter);
        if (!active) return;
        setSiteInboundLocationFilterOptions(
          locations.map((location) => ({ code: String(location.id), label: location.label }))
        );
      } catch (error) {
        captureError(error, 'qm-coordinator-loadSiteInboundLocationFilterOptions', {
          extra: { siteInboundSiteFilter }
        });
        if (active) {
          setSiteInboundLocationFilterOptions([]);
          setSiteInboundLocationFilter('');
        }
      }
    }

    void loadSiteInboundLocationFilterOptions();

    return () => {
      active = false;
    };
  }, [includeSiteInboundFilter, siteInboundSiteFilter]);

  const buildDashboardFilter = useCallback((): ListQmRecordsFilterInput => {
    const toApiDateTime = (value: string, endOfDay = false) => {
      if (!value) return null;
      const parsed = endOfDay ? dayjs(value).endOf('day') : dayjs(value).startOf('day');
      return parsed.isValid() ? parsed.toISOString() : null;
    };

    const types = [
      appliedFilters.includeInbound ? 'INBOUND' : null,
      appliedFilters.includeSiteInbound ? 'SITE_INBOUND' : null
    ].filter((value): value is 'INBOUND' | 'SITE_INBOUND' => Boolean(value));

    return {
      types,
      status: statusQuickFilter || null,
      reasonCode: appliedFilters.reasonCode || null,
      closureCode: appliedFilters.closureCode || null,
      createdBy: isNoRoleAccess ? normalizedUserEmail : appliedFilters.createdBy || null,
      itemLocation: appliedFilters.itemLocation || null,
      siteInboundSiteId: appliedFilters.siteInboundSiteId || null,
      siteInboundLocationId: appliedFilters.siteInboundLocationId ? Number(appliedFilters.siteInboundLocationId) : null,
      qmNumber: appliedFilters.qmNumber || null,
      poNumber: appliedFilters.poNumber || null,
      createdFrom: toApiDateTime(appliedFilters.createdFrom),
      createdTo: toApiDateTime(appliedFilters.createdTo, true),
      resolvedFrom: toApiDateTime(appliedFilters.resolvedFrom),
      resolvedTo: toApiDateTime(appliedFilters.resolvedTo, true),
      search: appliedFilters.search || null
    };
  }, [appliedFilters, isNoRoleAccess, normalizedUserEmail, statusQuickFilter]);

  const fetchDashboardSummaries = useCallback(
    async (filter: ListQmRecordsFilterInput) => {
      const summaryResult = await getQmDashboardSummaryRequest(filter);

      if (isNoRoleAccess && normalizedUserEmail) {
        const ownOnlyFilter = {
          types: ['INBOUND', 'SITE_INBOUND'] as Array<'INBOUND' | 'SITE_INBOUND'>,
          status: null,
          reasonCode: null,
          closureCode: null,
          createdBy: normalizedUserEmail,
          itemLocation: null,
          siteInboundSiteId: null,
          siteInboundLocationId: null,
          qmNumber: null,
          poNumber: null,
          createdFrom: null,
          createdTo: null,
          resolvedFrom: null,
          resolvedTo: null,
          search: null
        };
        const overallSummaryResult = await getQmDashboardSummaryRequest(ownOnlyFilter);
        return { summaryResult, overallSummaryResult };
      }

      return {
        summaryResult,
        overallSummaryResult: summaryResult
      };
    },
    [isNoRoleAccess, normalizedUserEmail]
  );

  const loadRows = useCallback(
    async (targetPage = 1) => {
      setIsLoading(true);
      try {
        const filter = buildDashboardFilter();

        if (isNoRoleAccess && !normalizedUserEmail) {
          setRows([]);
          setTotalCount(0);
          setSummary({ total: 0, created: 0, pending: 0, closed: 0 });
          setOverallSummary({ total: 0, created: 0, pending: 0, closed: 0 });
          setSelectedQm(null);
          return;
        }

        const [listResult, summaries] = await Promise.all([
          listQmRecordsRequest({ page: targetPage, pageSize: PAGE_SIZE, filter }),
          fetchDashboardSummaries(filter)
        ]);

        setRows(listResult.items || []);
        setTotalCount(listResult.totalCount || 0);
        setSummary(summaries.summaryResult);
        setOverallSummary(summaries.overallSummaryResult);
      } catch (error) {
        captureError(error, 'qm-coordinator-loadRows');
        alertRef.current('Unable to load QM dashboard data', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [buildDashboardFilter, fetchDashboardSummaries, isNoRoleAccess, normalizedUserEmail]
  );

  const refreshDashboardCounts = useCallback(async () => {
    try {
      const filter = buildDashboardFilter();

      if (isNoRoleAccess && !normalizedUserEmail) {
        setTotalCount(0);
        setSummary({ total: 0, created: 0, pending: 0, closed: 0 });
        setOverallSummary({ total: 0, created: 0, pending: 0, closed: 0 });
        return;
      }

      const { summaryResult, overallSummaryResult } = await fetchDashboardSummaries(filter);

      setSummary(summaryResult);
      setOverallSummary(overallSummaryResult);
      setTotalCount(summaryResult.total || 0);
    } catch (error) {
      captureError(error, 'qm-coordinator-refreshDashboardCounts');
    }
  }, [buildDashboardFilter, fetchDashboardSummaries, isNoRoleAccess, normalizedUserEmail]);

  const scheduleDashboardCountRefresh = useCallback(() => {
    if (dashboardCountRefreshTimerRef.current !== null) {
      window.clearTimeout(dashboardCountRefreshTimerRef.current);
    }

    dashboardCountRefreshTimerRef.current = window.setTimeout(() => {
      dashboardCountRefreshTimerRef.current = null;
      void refreshDashboardCounts();
    }, 700);
  }, [refreshDashboardCounts]);

  useEffect(() => {
    loadStaticOptions();
  }, [loadStaticOptions]);

  useEffect(() => {
    loadRows(page);
  }, [loadRows, page]);

  useEffect(() => {
    setPage(1);
  }, [statusQuickFilter]);

  useEffect(() => {
    return () => {
      if (dashboardCountRefreshTimerRef.current !== null) {
        window.clearTimeout(dashboardCountRefreshTimerRef.current);
      }
    };
  }, []);

  const selectedQmId = selectedQm?.qmId ?? null;

  useEffect(() => {
    selectedQmIdRef.current = selectedQmId;
  }, [selectedQmId]);

  useEffect(() => {
    isDetailDialogOpenRef.current = isDetailDialogOpen;
  }, [isDetailDialogOpen]);

  const doesLiveRecordMatchCurrentView = useCallback(
    (record: QmRecord) => {
      if (isNoRoleAccess && normalizedUserEmail) {
        const recordOwner = getNormalizedUserEmail(record.createdBy);
        if (recordOwner !== normalizedUserEmail) {
          return false;
        }
      }

      if (statusQuickFilter && record.status !== statusQuickFilter) {
        return false;
      }

      const recordType = String(record.type || '')
        .trim()
        .toUpperCase();
      if (!appliedFilters.includeInbound && !appliedFilters.includeSiteInbound) {
        return false;
      }
      if (recordType === 'INBOUND' && !appliedFilters.includeInbound) {
        return false;
      }
      if (recordType === 'SITE_INBOUND' && !appliedFilters.includeSiteInbound) {
        return false;
      }

      if (
        appliedFilters.reasonCode &&
        (record.reasonCode || '').trim().toLowerCase() !== appliedFilters.reasonCode.trim().toLowerCase() &&
        (record.reasonLabel || '').trim().toLowerCase() !== appliedFilters.reasonCode.trim().toLowerCase()
      ) {
        return false;
      }

      if (
        appliedFilters.closureCode &&
        (record.closureCode || '').trim().toLowerCase() !== appliedFilters.closureCode.trim().toLowerCase() &&
        (record.closureLabel || '').trim().toLowerCase() !== appliedFilters.closureCode.trim().toLowerCase()
      ) {
        return false;
      }

      if (
        appliedFilters.createdBy &&
        !(getNormalizedUserEmail(record.createdBy) || '').includes(
          getNormalizedUserEmail(appliedFilters.createdBy) || ''
        )
      ) {
        return false;
      }

      if (
        appliedFilters.itemLocation &&
        recordType === 'INBOUND' &&
        (record.itemLocation || '').trim().toLowerCase() !== appliedFilters.itemLocation.trim().toLowerCase()
      ) {
        return false;
      }

      if (
        appliedFilters.siteInboundSiteId &&
        recordType === 'SITE_INBOUND' &&
        (record.receivingWarehouseId || '').trim().toLowerCase() !==
          appliedFilters.siteInboundSiteId.trim().toLowerCase()
      ) {
        return false;
      }

      if (
        appliedFilters.siteInboundLocationId &&
        recordType === 'SITE_INBOUND' &&
        String(record.itemLocationId || '') !== appliedFilters.siteInboundLocationId
      ) {
        return false;
      }

      if (
        appliedFilters.qmNumber &&
        !(record.qmNumber || '').toLowerCase().includes(appliedFilters.qmNumber.toLowerCase())
      ) {
        return false;
      }

      if (
        appliedFilters.poNumber &&
        !(record.poNumber || '').toLowerCase().includes(appliedFilters.poNumber.toLowerCase())
      ) {
        return false;
      }

      const createdAt = dayjs(record.createdAt || undefined);
      if (appliedFilters.createdFrom) {
        const from = dayjs(appliedFilters.createdFrom).startOf('day');
        if (from.isValid() && createdAt.isValid() && createdAt.isBefore(from)) {
          return false;
        }
      }

      if (appliedFilters.createdTo) {
        const to = dayjs(appliedFilters.createdTo).endOf('day');
        if (to.isValid() && createdAt.isValid() && createdAt.isAfter(to)) {
          return false;
        }
      }

      const closedAt = dayjs(record.closedAt || undefined);
      if (appliedFilters.resolvedFrom) {
        const from = dayjs(appliedFilters.resolvedFrom).startOf('day');
        if (!closedAt.isValid() || (from.isValid() && closedAt.isBefore(from))) {
          return false;
        }
      }

      if (appliedFilters.resolvedTo) {
        const to = dayjs(appliedFilters.resolvedTo).endOf('day');
        if (!closedAt.isValid() || (to.isValid() && closedAt.isAfter(to))) {
          return false;
        }
      }

      if (appliedFilters.search) {
        const needle = normalizeSearchInput(appliedFilters.search).toLowerCase();
        if (!getQmSearchText(record).includes(needle)) {
          return false;
        }
      }

      return true;
    },
    [appliedFilters, isNoRoleAccess, normalizedUserEmail, statusQuickFilter]
  );

  const refreshSelectedQm = useCallback(async (qmId: number, fallbackRecord: QmRecord) => {
    try {
      const [latest, attachmentResults] = await Promise.all([
        getQmRecordRequest({ qmId }),
        listAttachmentsRequest(qmId)
      ]);
      if (!isDetailDialogOpenRef.current || selectedQmIdRef.current !== qmId) {
        return;
      }

      setSelectedQm(latest || fallbackRecord);
      setAttachments(attachmentResults || []);
    } catch (error) {
      console.error('Failed to refresh selected QM detail from live update:', error);
      captureError(error, 'qm-coordinator-refreshSelectedQm');

      if (!isDetailDialogOpenRef.current || selectedQmIdRef.current !== qmId) {
        return;
      }

      setSelectedQm(fallbackRecord);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeQmLiveUpdates(({ type, record, source }) => {
      console.info('QM live update received', {
        type,
        source,
        qmId: record.qmId,
        qmNumber: record.qmNumber
      });

      void (async () => {
        let nextRecord = record;
        const shouldRefreshDashboardCounts = type === 'CREATED' || type === 'UPDATED';

        if (type !== 'CREATED' && source === 'onPublishQmLiveUpdate') {
          try {
            const latest = await getQmRecordRequest({ qmId: record.qmId });
            if (latest) {
              nextRecord = latest;
            }
          } catch (error) {
            captureError(error, 'qm-coordinator-live-update-fetch-latest', {
              extra: { qmId: record.qmId, type }
            });
          }
        }

        const currentSelectedQmId = selectedQmIdRef.current;
        if (currentSelectedQmId && nextRecord.qmId === currentSelectedQmId) {
          void refreshSelectedQm(nextRecord.qmId, nextRecord);
        }

        setRows((prev) => {
          const index = prev.findIndex((item) => item.qmId === nextRecord.qmId);
          const matchesCurrentView = doesLiveRecordMatchCurrentView(nextRecord);

          if (index >= 0) {
            if (!matchesCurrentView) {
              return prev.filter((item) => item.qmId !== nextRecord.qmId);
            }

            const next = [...prev];
            next[index] = nextRecord;
            return next;
          }

          if ((type === 'CREATED' || source === 'onPublishQmLiveUpdate') && matchesCurrentView && page === 1) {
            return [nextRecord, ...prev].slice(0, PAGE_SIZE);
          }

          return prev;
        });

        if (shouldRefreshDashboardCounts) {
          scheduleDashboardCountRefresh();
        }
      })();
    });

    return () => {
      unsubscribe();
    };
  }, [doesLiveRecordMatchCurrentView, page, refreshSelectedQm, scheduleDashboardCountRefresh]);

  const openQmByLookup = useCallback(
    async ({ qmId, qmNumber }: { qmId?: number; qmNumber?: string }) => {
      const normalizedQmNumber = String(qmNumber || '').trim();
      if (!qmId && !normalizedQmNumber) return;

      setIsDetailDialogOpen(true);
      setIsDetailsLoading(true);
      setIsAttachmentsLoading(true);
      try {
        const full = await getQmRecordRequest({
          qmId,
          qmNumber: normalizedQmNumber || undefined
        });

        if (!full) {
          alert('QM record not found', 'warning');
          setIsDetailDialogOpen(false);
          return;
        }

        const attachmentResults = await listAttachmentsRequest(full.qmId);

        if (isNoRoleAccess && normalizedUserEmail) {
          const recordOwner = getNormalizedUserEmail(full.createdBy);
          if (recordOwner !== normalizedUserEmail) {
            alert('You can only open QM records created by your email.', 'warning');
            setIsDetailDialogOpen(false);
            return;
          }
        }

        setAttachments(attachmentResults || []);
        setSelectedQm(full);
        setEditStatus(full.status);
        setEditReasonCode(full.reasonCode || '');
        setEditSupplierName(full.supplierName || '');
        setEditContactName(full.contactName || '');
        setEditSiteInboundSiteId(full.type === 'SITE_INBOUND' ? String(full.receivingWarehouseId || '') : '');
        setEditItemLocation(
          full.type === 'SITE_INBOUND' && full.itemLocationId ? String(full.itemLocationId) : full.itemLocation || ''
        );
        setEditOtherInformation(full.otherInformation || '');
        setEditDescription(full.description || '');
        setEditClosureCode(full.closureCode || '');
        setEditClosureNotes(full.closureNotes || '');

        try {
          const scopedReasons = await listQmReferenceReasonsRequest(full.type);
          const mappedScopedReasons = scopedReasons.map((item) => ({ code: item.code, label: item.label }));

          if (full.reasonCode && !mappedScopedReasons.some((item) => item.code === full.reasonCode)) {
            mappedScopedReasons.unshift({
              code: full.reasonCode,
              label: full.reasonLabel || full.reasonCode
            });
          }

          setEditReasonOptions(mappedScopedReasons);
        } catch (error) {
          captureError(error, 'qm-coordinator-openQm-reasons');
          setEditReasonOptions(reasonOptions);
          alert('Unable to load QM reason options for this QM type', 'warning');
        }

        if (full.reasonCode) {
          const closureCodes = await listQmReferenceClosureCodesRequest(full.reasonCode);
          setClosureOptions(closureCodes.map((item) => ({ code: item.code, label: item.label })));
        } else {
          setClosureOptions([]);
        }
      } catch (error) {
        captureError(error, 'qm-coordinator-openQm', {
          extra: { qmId, qmNumber: normalizedQmNumber || null }
        });
        alert('Unable to load QM details', 'error');
      } finally {
        setIsDetailsLoading(false);
        setIsAttachmentsLoading(false);
      }
    },
    [alert, isNoRoleAccess, normalizedUserEmail, reasonOptions]
  );

  async function openQm(qm: QmRecord) {
    const qmNumber = String(qm.qmNumber || qm.qmId).trim();
    openedQmFromUrlRef.current = `qm:${qmNumber.toLowerCase()}`;
    navigate(`/qm-cases?qm=${encodeURIComponent(qmNumber)}`);
    await openQmByLookup({ qmId: qm.qmId });
  }

  useEffect(() => {
    const params = new URLSearchParams(routeLocation.search);
    const rawQmNumber = String(params.get('qm') || '').trim();
    if (rawQmNumber) {
      const key = `qm:${rawQmNumber.toLowerCase()}`;
      if (openedQmFromUrlRef.current === key) return;

      openedQmFromUrlRef.current = key;
      void openQmByLookup({ qmNumber: rawQmNumber });
      return;
    }

    const rawQmId = params.get('qmId');
    const qmId = Number(rawQmId);
    if (!rawQmId || !Number.isInteger(qmId) || qmId <= 0) return;

    const key = `qmId:${qmId}`;
    if (openedQmFromUrlRef.current === key) return;

    openedQmFromUrlRef.current = key;
    void openQmByLookup({ qmId });
  }, [openQmByLookup, routeLocation.search]);

  function closeDetailDialog(options?: { skipUrlCleanup?: boolean }) {
    setIsDetailDialogOpen(false);
    setIsReprintDialogOpen(false);
    setSelectedQm(null);
    setSelectedPreview(null);
    setAttachments([]);
    setEditReasonOptions(reasonOptions);
    setEditSiteInboundSiteId('');
    openedQmFromUrlRef.current = null;

    if (options?.skipUrlCleanup) return;

    const params = new URLSearchParams(routeLocation.search);
    if (params.has('qm') || params.has('qmId')) {
      params.delete('qm');
      params.delete('qmId');
      const nextSearch = params.toString();
      navigate(nextSearch ? `/qm-cases?${nextSearch}` : '/qm-cases', { replace: true });
    }
  }

  const handleEditReasonCodeChange = useCallback(
    async (value: string) => {
      setEditReasonCode(value);

      if (!value) {
        setClosureOptions([]);
        setEditClosureCode('');
        return;
      }

      try {
        const closureCodes = await listQmReferenceClosureCodesRequest(value);
        const mapped = closureCodes.map((item) => ({ code: item.code, label: item.label }));
        setClosureOptions(mapped);
        setEditClosureCode((previous) => (mapped.some((item) => item.code === previous) ? previous : ''));
      } catch (error) {
        captureError(error, 'qm-coordinator-handleEditReasonCodeChange');
        alert('Unable to load QM closure options for selected reason', 'warning');
      }
    },
    [alert]
  );

  const handleEditSiteInboundSiteIdChange = useCallback((value: string) => {
    setEditSiteInboundSiteId(value);
    setEditItemLocation('');
  }, []);

  async function saveQmStatusEdits(): Promise<boolean> {
    if (!selectedQm || !canEditSelectedQm) return false;

    if (selectedQm.status === 'CLOSED') {
      alert('Closed QMs are read-only.', 'warning');
      return false;
    }

    const statusChanged = editStatus !== selectedQm.status;
    const existingClosureCode = selectedQm.closureCode || null;
    const nextClosureCode = editClosureCode || null;
    const existingClosureNotes = (selectedQm.closureNotes || '').trim() || null;
    const nextClosureNotes = editClosureNotes.trim() || null;
    const isClosing = editStatus === 'CLOSED';
    const closureCodeChanged = isClosing && nextClosureCode !== existingClosureCode;
    const closureNotesChanged = isClosing && nextClosureNotes !== existingClosureNotes;

    if (isClosing && !nextClosureCode) {
      alert('Closure Code is required when closing a QM.', 'warning');
      return false;
    }

    if (!statusChanged && !closureCodeChanged && !closureNotesChanged) {
      alert('No status changes to save.', 'info');
      return false;
    }

    const updateInput: Parameters<typeof updateQmRecordRequest>[0] = {
      qmId: selectedQm.qmId,
      note: 'Coordinator update',
      updatedBy: normalizedUserEmail
    };

    if (statusChanged) {
      updateInput.status = editStatus;
    }

    if (closureCodeChanged) {
      updateInput.closureCode = nextClosureCode;
    }

    if (closureNotesChanged) {
      updateInput.closureNotes = nextClosureNotes;
    }

    try {
      const updated = await updateQmRecordRequest(updateInput);

      setSelectedQm(updated);
      setEditSiteInboundSiteId(updated.type === 'SITE_INBOUND' ? String(updated.receivingWarehouseId || '') : '');
      setRows((prev) => prev.map((item) => (item.qmId === updated.qmId ? updated : item)));
      alert('QM updated', 'success');
      await loadRows(page);
      return true;
    } catch (error) {
      captureError(error, 'qm-coordinator-saveQmStatusEdits', {
        extra: { qmId: selectedQm.qmId }
      });
      alert('Unable to update QM', 'error');
      return false;
    }
  }

  async function saveQmDetailEdits(): Promise<boolean> {
    if (!selectedQm || !canEditSelectedQm) return false;

    if (selectedQm.status === 'CLOSED') {
      alert('Closed QMs are read-only.', 'warning');
      return false;
    }

    const canEditCreatedOnlyFields = selectedQm.status === 'CREATED';

    const normalizedContactName = editContactName.trim() || null;
    const normalizedSupplierName = editSupplierName.trim() || null;
    const normalizedOtherInformation = editOtherInformation.trim() || null;
    const normalizedDescription = editDescription.trim() || null;
    const existingSupplierName = (selectedQm.supplierName || '').trim() || null;
    const existingContactName = (selectedQm.contactName || '').trim() || null;
    const existingOtherInformation = (selectedQm.otherInformation || '').trim() || null;
    const existingDescription = (selectedQm.description || '').trim() || null;

    const updateInput: Parameters<typeof updateQmRecordRequest>[0] = {
      qmId: selectedQm.qmId,
      note: 'Coordinator update',
      updatedBy: normalizedUserEmail
    };

    if (canEditCreatedOnlyFields) {
      const normalizedReasonCode = editReasonCode || undefined;
      if (normalizedReasonCode && normalizedReasonCode !== selectedQm.reasonCode) {
        const isChangingFromAttachmentOptionalReason = SITE_INBOUND_ATTACHMENT_OPTIONAL_REASON_CODES.has(
          selectedQm.reasonCode
        );
        const isChangingToAttachmentRequiredReason =
          !SITE_INBOUND_ATTACHMENT_OPTIONAL_REASON_CODES.has(normalizedReasonCode);
        if (
          selectedQm.type === 'SITE_INBOUND' &&
          isChangingFromAttachmentOptionalReason &&
          isChangingToAttachmentRequiredReason
        ) {
          if (isAttachmentsLoading) {
            alert('Attachments are still loading. Please try again in a moment.', 'warning');
            return false;
          }
          if (attachments.length === 0) {
            alert(
              'The new reason you selected needs an attachment. Please upload at least one attachment before changing this QM reason.',
              'warning'
            );
            return false;
          }
        }
        updateInput.reasonCode = normalizedReasonCode;
      }

      if (selectedQm.type === 'SITE_INBOUND') {
        const normalizedItemLocationId = Number(editItemLocation || 0);
        if (
          Number.isInteger(normalizedItemLocationId) &&
          normalizedItemLocationId > 0 &&
          normalizedItemLocationId !== selectedQm.itemLocationId
        ) {
          updateInput.itemLocationId = normalizedItemLocationId;
        }
      } else {
        const normalizedItemLocation = editItemLocation || undefined;
        if (normalizedItemLocation && normalizedItemLocation !== selectedQm.itemLocation) {
          updateInput.itemLocation = normalizedItemLocation;
        }
      }
    }

    if (normalizedSupplierName !== existingSupplierName) {
      updateInput.supplierName = normalizedSupplierName;
    }

    if (normalizedContactName !== existingContactName) {
      updateInput.contactName = normalizedContactName;
    }

    if (normalizedOtherInformation !== existingOtherInformation) {
      updateInput.otherInformation = normalizedOtherInformation;
    }

    if (normalizedDescription !== existingDescription) {
      updateInput.description = normalizedDescription;
    }

    const changedKeys = Object.keys(updateInput).filter((key) => !['qmId', 'note', 'updatedBy'].includes(key));
    if (changedKeys.length === 0) {
      alert('No detail changes to save.', 'info');
      return false;
    }

    try {
      const updated = await updateQmRecordRequest(updateInput);

      setSelectedQm(updated);
      setRows((prev) => prev.map((item) => (item.qmId === updated.qmId ? updated : item)));
      alert('QM details updated', 'success');
      await loadRows(page);
      return true;
    } catch (error) {
      captureError(error, 'qm-coordinator-saveQmDetailEdits', {
        extra: { qmId: selectedQm.qmId }
      });
      alert('Unable to update QM details', 'error');
      return false;
    }
  }

  async function addActionComment() {
    if (!selectedQm || !canEditSelectedQm || isAddingActionComment) return;
    const body = actionComment.trim();
    if (!body) return;

    setIsAddingActionComment(true);
    try {
      const updated = await addQmCommentRequest({
        qmId: selectedQm.qmId,
        body,
        createdBy: normalizedUserEmail
      });

      setSelectedQm(updated);
      setActionComment('');
      setRows((prev) => prev.map((item) => (item.qmId === updated.qmId ? updated : item)));
      alert('Action comment added', 'success');
    } catch (error) {
      captureError(error, 'qm-coordinator-addActionComment', {
        extra: { qmId: selectedQm.qmId }
      });
      alert('Unable to add action comment', 'error');
    } finally {
      setIsAddingActionComment(false);
    }
  }

  async function uploadDetailAttachments(files: File[]) {
    if (!selectedQm || !canEditSelectedQm) return;
    if (!files.length) return;

    const qmId = selectedQm.qmId;
    const successfulFiles: string[] = [];
    const failedFiles: string[] = [];
    const UPLOAD_CONCURRENCY = 10;

    setIsUploadingAttachments(true);

    try {
      const uploadSingleFile = async (originalFile: File) => {
        const file = await compressImageForUpload(originalFile);

        const { uploadUrl, s3Key } = await getUploadUrlRequest({
          qmId,
          fileName: file.name,
          mimeType: file.type || null
        });

        await uploadFileToS3(uploadUrl, file);

        await confirmAttachmentRequest({
          qmId,
          s3Key,
          fileName: file.name,
          mimeType: file.type || null,
          sizeBytes: file.size,
          uploadedBy: normalizedUserEmail
        });

        return file.name;
      };

      for (let index = 0; index < files.length; index += UPLOAD_CONCURRENCY) {
        const batch = files.slice(index, index + UPLOAD_CONCURRENCY);
        const results = await Promise.allSettled(batch.map((file) => uploadSingleFile(file)));

        results.forEach((result, resultIndex) => {
          const fileName = batch[resultIndex]?.name || 'unknown-file';

          if (result.status === 'fulfilled') {
            successfulFiles.push(result.value);
            return;
          }

          failedFiles.push(fileName);
          captureError(result.reason, 'qm-coordinator-uploadDetailAttachments', {
            extra: { qmId, fileName }
          });
        });
      }

      const refreshedAttachments = await listAttachmentsRequest(qmId);
      setAttachments(refreshedAttachments || []);

      if (successfulFiles.length > 0) {
        try {
          const refreshedQm = await getQmRecordRequest({ qmId });
          if (refreshedQm) {
            setSelectedQm(refreshedQm);
            setRows((prev) => prev.map((item) => (item.qmId === refreshedQm.qmId ? refreshedQm : item)));
          }
        } catch (error) {
          captureError(error, 'qm-coordinator-uploadDetailAttachments-refreshQm', {
            extra: { qmId }
          });
        }
      }

      if (successfulFiles.length > 0 && failedFiles.length === 0) {
        alert(`${successfulFiles.length} file(s) uploaded.`, 'success');
        return;
      }

      if (successfulFiles.length > 0 && failedFiles.length > 0) {
        alert(
          `${successfulFiles.length} file(s) uploaded. Failed: ${failedFiles.length} (${failedFiles.join(', ')})`,
          'warning'
        );
        return;
      }

      alert('Unable to upload files.', 'error');
    } finally {
      setIsUploadingAttachments(false);
    }
  }

  function applyFilters() {
    const normalizedSearch = normalizeSearchInput(searchFilter);
    const nextAppliedFilters: DashboardFilters = {
      includeInbound: includeInboundFilter,
      includeSiteInbound: includeSiteInboundFilter,
      reasonCode: reasonFilter,
      closureCode: closureFilter,
      createdBy: createdByFilter,
      itemLocation: includeInboundFilter ? locationFilter : '',
      siteInboundSiteId: includeSiteInboundFilter ? siteInboundSiteFilter : '',
      siteInboundLocationId: includeSiteInboundFilter ? siteInboundLocationFilter : '',
      qmNumber: qmNumberFilter,
      poNumber: poNumberFilter,
      createdFrom: createdFromFilter,
      createdTo: createdToFilter,
      resolvedFrom: resolvedFromFilter,
      resolvedTo: resolvedToFilter,
      search: normalizedSearch
    };

    const isUnchanged =
      appliedFilters.includeInbound === nextAppliedFilters.includeInbound &&
      appliedFilters.includeSiteInbound === nextAppliedFilters.includeSiteInbound &&
      appliedFilters.reasonCode === nextAppliedFilters.reasonCode &&
      appliedFilters.closureCode === nextAppliedFilters.closureCode &&
      appliedFilters.createdBy === nextAppliedFilters.createdBy &&
      appliedFilters.itemLocation === nextAppliedFilters.itemLocation &&
      appliedFilters.siteInboundSiteId === nextAppliedFilters.siteInboundSiteId &&
      appliedFilters.siteInboundLocationId === nextAppliedFilters.siteInboundLocationId &&
      appliedFilters.qmNumber === nextAppliedFilters.qmNumber &&
      appliedFilters.poNumber === nextAppliedFilters.poNumber &&
      appliedFilters.createdFrom === nextAppliedFilters.createdFrom &&
      appliedFilters.createdTo === nextAppliedFilters.createdTo &&
      appliedFilters.resolvedFrom === nextAppliedFilters.resolvedFrom &&
      appliedFilters.resolvedTo === nextAppliedFilters.resolvedTo &&
      appliedFilters.search === nextAppliedFilters.search;

    if (!isUnchanged) {
      setAppliedFilters(nextAppliedFilters);
    }

    setPage(1);
  }

  function resetFilters() {
    setIncludeInboundFilter(true);
    setIncludeSiteInboundFilter(true);
    setReasonFilter('');
    setClosureFilter('');
    setCreatedByFilter('');
    setLocationFilter('');
    setSiteInboundSiteFilter('');
    setSiteInboundLocationFilter('');
    setSiteInboundLocationFilterOptions([]);
    setQmNumberFilter('');
    setPoNumberFilter('');
    setCreatedFromFilter('');
    setCreatedToFilter('');
    setResolvedFromFilter('');
    setResolvedToFilter('');
    setSearchFilter('');
    setAppliedFilters(EMPTY_DASHBOARD_FILTERS);
    setPage(1);
  }

  function setTypeQuickFilter(nextIncludeInbound: boolean, nextIncludeSiteInbound: boolean) {
    setIncludeInboundFilter(nextIncludeInbound);
    setIncludeSiteInboundFilter(nextIncludeSiteInbound);

    if (!nextIncludeInbound) {
      setLocationFilter('');
    }

    if (!nextIncludeSiteInbound) {
      setSiteInboundSiteFilter('');
      setSiteInboundLocationFilter('');
      setSiteInboundLocationFilterOptions([]);
    }

    setAppliedFilters((current) => ({
      ...current,
      includeInbound: nextIncludeInbound,
      includeSiteInbound: nextIncludeSiteInbound,
      itemLocation: nextIncludeInbound ? current.itemLocation : '',
      siteInboundSiteId: nextIncludeSiteInbound ? current.siteInboundSiteId : '',
      siteInboundLocationId: nextIncludeSiteInbound ? current.siteInboundLocationId : ''
    }));
    setPage(1);
  }

  async function reprintLabels() {
    if (!selectedQm) return;
    if (!canEditSelectedQm) {
      alert('You can only reprint labels for QMs created by you unless you have full edit access.', 'warning');
      return;
    }

    const packageCount = Number(selectedQm.packageCount || 0);
    if (!Number.isFinite(packageCount) || packageCount <= 0) {
      alert('No package count found on this QM, so labels cannot be reprinted.', 'warning');
      return;
    }

    setReprintMode('ALL');
    setReprintLabelNumber('1');
    setIsReprintDialogOpen(true);
  }

  async function confirmReprintLabels() {
    if (!selectedQm) return;

    const packageCount = Number(selectedQm.packageCount || 0);
    if (!Number.isFinite(packageCount) || packageCount <= 0) {
      alert('No package count found on this QM, so labels cannot be reprinted.', 'warning');
      return;
    }

    const selectedPackageNumbers =
      reprintMode === 'ALL'
        ? Array.from({ length: packageCount }, (_, index) => index + 1)
        : [Number(reprintLabelNumber)];

    if (
      reprintMode === 'ONE' &&
      (!Number.isInteger(selectedPackageNumbers[0]) ||
        selectedPackageNumbers[0] < 1 ||
        selectedPackageNumbers[0] > packageCount)
    ) {
      alert(`Please enter a valid label number between 1 and ${packageCount}.`, 'warning');
      return;
    }

    setIsReprintDialogOpen(false);

    setIsReprintingLabels(true);

    try {
      const userPrinter = await store.printers.getPrinterForUser(user.cognitoUser.username);

      if (!userPrinter) {
        alert('Error getting printer, please set your printer and try again', 'error');
        return;
      }

      const createdAt = new Date(selectedQm.createdAt);
      const formattedCreatedAt = Number.isNaN(createdAt.getTime())
        ? selectedQm.createdAt
        : createdAt.toLocaleDateString('en-AU');

      const qmPrintEvents: QMPrintProps[] = selectedPackageNumbers.map((packageNumber) => ({
        createdAt: formattedCreatedAt,
        createdBy: String(selectedQm.createdBy || normalizedUserEmail || user.name || '').trim(),
        packageNumber: String(packageNumber),
        totalPackages: String(packageCount),
        qmNumber: String(selectedQm.qmNumber || ''),
        poNumber: String(selectedQm.poNumber || ''),
        reason: String(selectedQm.reasonLabel || '').trim(),
        issue: '-',
        locationName: getQmLocationLabel(selectedQm),
        description: String(selectedQm.description || '').trim()
      }));

      const successfulLabels: string[] = [];
      for (const printEvent of qmPrintEvents) {
        const printResponse = await printQm([printEvent], userPrinter.printerIP, userPrinter.printerPort);

        if (printResponse.error) {
          const partialSuccessMessage =
            successfulLabels.length > 0 ? ` Labels printed before failure: ${successfulLabels.join(', ')}.` : '';
          alert(
            `Label ${printEvent.packageNumber} reprint failed for QM ${selectedQm.qmNumber}.${partialSuccessMessage}`,
            'warning'
          );
          return;
        }

        successfulLabels.push(printEvent.packageNumber);
      }

      alert(`Label printed for QM ${selectedQm.qmNumber}.`, 'success');
    } catch (error) {
      captureError(error, 'qm-coordinator-confirmReprintLabels', {
        extra: { qmId: selectedQm.qmId }
      });
      alert('Unable to reprint labels', 'error');
    } finally {
      setIsReprintingLabels(false);
    }
  }

  const detailDialogModel: QmDetailDialogModel = {
    selectedQm,
    isDetailsLoading,
    canEdit: canEditSelectedQm,
    canUploadAttachments: canEditSelectedQm,
    canReprintLabels: canEditSelectedQm,
    showFilteredOutNotice,
    sectionCardSx,
    attachments,
    isAttachmentsLoading,
    isUploadingAttachments,
    onUploadAttachments: uploadDetailAttachments,
    onSelectPreview: setSelectedPreview,
    actionComment,
    isAddingActionComment,
    onActionCommentChange: setActionComment,
    addActionComment,
    onReprintLabels: reprintLabels,
    isReprintingLabels,
    editStatus,
    onEditStatusChange: setEditStatus,
    editReasonCode,
    onEditReasonCodeChange: handleEditReasonCodeChange,
    editSupplierName,
    onEditSupplierNameChange: setEditSupplierName,
    editContactName,
    onEditContactNameChange: setEditContactName,
    editSiteInboundSiteId,
    onEditSiteInboundSiteIdChange: handleEditSiteInboundSiteIdChange,
    editItemLocation,
    onEditItemLocationChange: setEditItemLocation,
    editOtherInformation,
    onEditOtherInformationChange: setEditOtherInformation,
    editDescription,
    onEditDescriptionChange: setEditDescription,
    editClosureCode,
    onEditClosureCodeChange: setEditClosureCode,
    editClosureNotes,
    onEditClosureNotesChange: setEditClosureNotes,
    saveQmStatusEdits,
    saveQmDetailEdits,
    closureOptions,
    reasonOptions,
    editReasonOptions,
    siteInboundSiteOptions: siteInboundSites.map((site) => ({
      code: String(site.siteId),
      label: site.alias ? `${site.label} (${site.alias})` : site.label
    })),
    locationOptions: detailLocationOptions,
    getLocationLabel,
    statusOptions: STATUS_OPTIONS,
    formatStatusLabel,
    getQmTypeLabel,
    onCloseForNavigation: () => closeDetailDialog({ skipUrlCleanup: true }),
    siteInboundCoordinatorAssignmentLabel: selectedQmCoordinatorSite?.display ?? null
  };

  if (isQmFeatureFlagsLoading) {
    return (
      <Box
        sx={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '72px' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isQmNewDashboardEnabled) {
    return (
      <Box
        sx={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '72px' }}>
        <Alert severity="info" sx={{ maxWidth: 680 }}>
          QM Cases is currently disabled. Please use Raise a QM from the header, or contact an administrator to enable
          the QM Cases feature.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        overflowX: 'hidden',
        overflowY: { xs: 'auto', md: 'hidden' },
        '@media (max-height: 860px)': {
          overflowY: 'auto'
        },
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Box
        sx={{
          flex: '0 0 auto',
          marginTop: '72px',
          px: { xs: 2, md: 3 },
          py: { xs: 0.4, md: 0.55 },
          backgroundColor: 'transparent',
          borderBottom: 'none'
        }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 0.25, md: 1 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ width: 'min(1760px, calc(100vw - 32px))', margin: '0 auto', px: { xs: 1.5, md: 2.5 } }}>
          <Typography
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.25rem', md: '1.38rem' },
              lineHeight: 1.2
            }}>
            QM Cases
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.78rem', md: '0.82rem' },
                fontWeight: 600,
                px: 1,
                py: 0.35,
                borderRadius: 999,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider'
              }}>
              Role: {getQmListRoleLabel(qmListRole)}
            </Typography>
            {siteInboundCoordinatorSites.length > 0 ? (
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={() => setIsSiteRolesDialogOpen(true)}
                sx={{
                  color: 'primary.main',
                  fontSize: { xs: '0.78rem', md: '0.82rem' },
                  fontWeight: 700,
                  px: 1,
                  py: 0.35,
                  minHeight: 0,
                  minWidth: 0,
                  borderRadius: 999,
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: 'rgba(25, 118, 210, 0.28)',
                  textTransform: 'none',
                  lineHeight: 1.2,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.14)',
                    borderColor: 'primary.main'
                  }
                }}
                title={`Site Inbound QM Site Specific Coordinators: ${siteInboundCoordinatorSites
                  .map((site) => site.display)
                  .join(', ')}`}>
                {siteRoleSummary}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: { xs: 'visible', md: 'hidden' },
          '@media (max-height: 860px)': {
            overflow: 'visible'
          }
        }}>
        <Box sx={{ width: 'min(1760px, calc(100vw - 32px))', mx: 'auto', px: { xs: 1.5, md: 2.5 }, height: '100%' }}>
          <Stack
            spacing={2}
            sx={{
              padding: '1em 0',
              height: '100%',
              minHeight: 0,
              overflow: { xs: 'visible', md: 'hidden' },
              '@media (max-height: 860px)': {
                overflow: 'visible'
              }
            }}>
            {isNoRoleAccess ? (
              <Alert severity="info" sx={{ flexShrink: 0 }}>
                Own-only access: you can create, view, and edit workflow fields for your own QMs only.
              </Alert>
            ) : !canEditAll ? (
              <Alert severity="info" sx={{ flexShrink: 0 }}>
                Read-all access: you can view all QMs and edit workflow fields for your own QMs
                {siteInboundCoordinatorSites.length > 0
                  ? ' and eligible Site Inbound QMs for your assigned sites.'
                  : ' only.'}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ flexShrink: 0 }}>
              <Paper
                sx={{
                  p: 1.4,
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: 0.3,
                    mb: 0.85
                  }}>
                  Status
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    gap: 0.5,
                    p: 0.4,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#f8fafc'
                  }}>
                  {[
                    { value: '', label: 'All', activeBg: '#e2e8f0', activeColor: '#334155' },
                    { value: 'CREATED', label: 'Open', activeBg: '#dcfce7', activeColor: '#166534' },
                    { value: 'PENDING', label: 'Pending', activeBg: '#fde68a', activeColor: '#92400e' },
                    { value: 'CLOSED', label: 'Closed', activeBg: '#e5e7eb', activeColor: '#374151' }
                  ].map((item) => {
                    const selected = statusQuickFilter === item.value;

                    return (
                      <Box
                        key={item.label}
                        component="button"
                        type="button"
                        onClick={() => setStatusQuickFilter(item.value as QmCrudStatus | '')}
                        sx={{
                          height: 30,
                          border: '1px solid',
                          borderColor: selected ? item.activeColor : 'rgba(148, 163, 184, 0.3)',
                          borderRadius: 1.2,
                          backgroundColor: selected ? item.activeBg : '#f1f5f9',
                          color: selected ? item.activeColor : '#475569',
                          fontSize: '0.74rem',
                          fontWeight: selected ? 700 : 600,
                          letterSpacing: 0.15,
                          cursor: 'pointer',
                          transition: 'all 140ms ease',
                          boxShadow: selected ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                          '&:hover': {
                            backgroundColor: selected ? item.activeBg : '#f8fafc',
                            borderColor: selected ? item.activeColor : 'rgba(100, 116, 139, 0.44)'
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            boxShadow: `0 0 0 2px rgba(255, 255, 255, 1), 0 0 0 3px ${selected ? item.activeColor : '#94a3b8'}`
                          }
                        }}>
                        {item.label}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
              <Paper
                sx={{
                  p: 1.4,
                  flex: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: 0.3,
                    mb: 0.85
                  }}>
                  Type
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 0.5,
                    p: 0.4,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#f8fafc'
                  }}>
                  {[
                    {
                      value: 'ALL',
                      label: 'All',
                      includeInbound: true,
                      includeSiteInbound: true,
                      activeBg: '#e2e8f0',
                      activeColor: '#334155'
                    },
                    {
                      value: 'INBOUND',
                      label: 'Inbound',
                      includeInbound: true,
                      includeSiteInbound: false,
                      activeBg: '#dbeafe',
                      activeColor: '#1d4ed8'
                    },
                    {
                      value: 'SITE_INBOUND',
                      label: 'Site Inbound',
                      includeInbound: false,
                      includeSiteInbound: true,
                      activeBg: '#dcfce7',
                      activeColor: '#166534'
                    }
                  ].map((item) => {
                    const selected =
                      appliedFilters.includeInbound === item.includeInbound &&
                      appliedFilters.includeSiteInbound === item.includeSiteInbound;

                    return (
                      <Box
                        key={item.value}
                        component="button"
                        type="button"
                        onClick={() => setTypeQuickFilter(item.includeInbound, item.includeSiteInbound)}
                        sx={{
                          height: 30,
                          border: '1px solid',
                          borderColor: selected ? item.activeColor : 'rgba(148, 163, 184, 0.3)',
                          borderRadius: 1.2,
                          backgroundColor: selected ? item.activeBg : '#f1f5f9',
                          color: selected ? item.activeColor : '#475569',
                          fontSize: '0.74rem',
                          fontWeight: selected ? 700 : 600,
                          letterSpacing: 0.15,
                          cursor: 'pointer',
                          transition: 'all 140ms ease',
                          boxShadow: selected ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                          '&:hover': {
                            backgroundColor: selected ? item.activeBg : '#f8fafc',
                            borderColor: selected ? item.activeColor : 'rgba(100, 116, 139, 0.44)'
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            boxShadow: `0 0 0 2px rgba(255, 255, 255, 1), 0 0 0 3px ${selected ? item.activeColor : '#94a3b8'}`
                          }
                        }}>
                        {item.label}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
              {[
                { label: 'Total', value: summary.total, total: overallSummary.total, tone: '#334155' },
                { label: 'Open', value: summary.created, total: overallSummary.created, tone: '#0f766e' },
                { label: 'Pending', value: summary.pending, total: overallSummary.pending, tone: '#92400e' },
                { label: 'Closed', value: summary.closed, total: overallSummary.closed, tone: '#1d4ed8' }
              ].map((item) => (
                <Paper
                  key={item.label}
                  sx={{
                    p: 1.4,
                    flex: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                  }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3 }}>
                      {item.label}
                    </Typography>
                    {activeAppliedFilterCount > 0 ? (
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary' }}>
                        filtered / total
                      </Typography>
                    ) : null}
                  </Stack>

                  <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 0.45 }}>
                    <Typography
                      sx={{
                        fontSize: '1.55rem',
                        lineHeight: 1,
                        fontWeight: 700,
                        color: item.tone,
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                      {item.value.toLocaleString()}
                    </Typography>
                    {activeAppliedFilterCount > 0 ? (
                      <>
                        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', fontWeight: 600 }}>/</Typography>
                        <Typography
                          sx={{
                            fontSize: '1.02rem',
                            lineHeight: 1.1,
                            fontWeight: 600,
                            color: 'text.secondary',
                            fontVariantNumeric: 'tabular-nums'
                          }}>
                          {item.total.toLocaleString()}
                        </Typography>
                      </>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Paper
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                flexShrink: 0
              }}>
              <Stack
                spacing={1.25}
                sx={{
                  '& .MuiInputBase-input': { fontSize: '0.84rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.84rem' },
                  '& .MuiFormHelperText-root': { fontSize: '0.72rem' }
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: 'text.secondary' }}>
                    Filters
                  </Typography>
                  <Chip
                    size="small"
                    variant={activeAppliedFilterCount > 0 ? 'filled' : 'outlined'}
                    color={activeAppliedFilterCount > 0 ? 'primary' : 'default'}
                    label={
                      activeAppliedFilterCount > 0
                        ? `${activeAppliedFilterCount} active filter${activeAppliedFilterCount > 1 ? 's' : ''}`
                        : 'No active filters'
                    }
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Reason"
                    value={reasonFilter}
                    sx={getAppliedFilterFieldSx(!!appliedFilters.reasonCode)}
                    onChange={(event) => setReasonFilter(event.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {reasonFilterOptions.map((reason) => (
                      <MenuItem key={reason.code} value={reason.code}>
                        {reason.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Closure"
                    value={closureFilter}
                    sx={getAppliedFilterFieldSx(!!appliedFilters.closureCode)}
                    onChange={(event) => setClosureFilter(event.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {closureFilterOptions.map((closure) => (
                      <MenuItem key={closure.code} value={closure.code}>
                        {closure.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {includeInboundFilter && (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Inbound Location"
                      value={locationFilter}
                      sx={getAppliedFilterFieldSx(!!appliedFilters.itemLocation)}
                      onChange={(event) => setLocationFilter(event.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      {locationOptions.map((location) => (
                        <MenuItem key={location.code} value={location.code}>
                          {location.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {includeSiteInboundFilter && (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Site"
                      value={siteInboundSiteFilter}
                      sx={getAppliedFilterFieldSx(!!appliedFilters.siteInboundSiteId)}
                      onChange={(event) => {
                        setSiteInboundSiteFilter(event.target.value);
                        setSiteInboundLocationFilter('');
                      }}>
                      <MenuItem value="">All</MenuItem>
                      {siteInboundSites.map((site) => (
                        <MenuItem key={site.siteId} value={site.siteId}>
                          {site.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {includeSiteInboundFilter && siteInboundSiteFilter && (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Site Inbound Location"
                      value={siteInboundLocationFilter}
                      sx={getAppliedFilterFieldSx(!!appliedFilters.siteInboundLocationId)}
                      onChange={(event) => setSiteInboundLocationFilter(event.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      {siteInboundLocationFilterOptions.map((location) => (
                        <MenuItem key={location.code} value={location.code}>
                          {location.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    label="QM #"
                    value={qmNumberFilter}
                    sx={getAppliedFilterFieldSx(!!appliedFilters.qmNumber)}
                    onChange={(event) => setQmNumberFilter(event.target.value)}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="PO Number"
                    value={poNumberFilter}
                    sx={getAppliedFilterFieldSx(!!appliedFilters.poNumber)}
                    onChange={(event) => setPoNumberFilter(event.target.value)}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    value={searchFilter}
                    sx={getAppliedFilterFieldSx(!!appliedFilters.search)}
                    onChange={(event) => setSearchFilter(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        applyFilters();
                      }
                    }}
                    placeholder="QM, PO, SSCC, ASN, description, action comment, additional details"
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Created By Email"
                    value={createdByFilter}
                    disabled={isNoRoleAccess}
                    sx={getAppliedFilterFieldSx(!!appliedFilters.createdBy)}
                    onChange={(event) => setCreatedByFilter(getNormalizedUserEmail(event.target.value) || '')}
                    placeholder="user@mrl.com.au"
                  />
                  <DateRangeFilter
                    label="Created"
                    from={createdFromFilter}
                    to={createdToFilter}
                    applied={!!(appliedFilters.createdFrom || appliedFilters.createdTo)}
                    onFromChange={setCreatedFromFilter}
                    onToChange={setCreatedToFilter}
                  />
                  <DateRangeFilter
                    label="Resolved"
                    from={resolvedFromFilter}
                    to={resolvedToFilter}
                    applied={!!(appliedFilters.resolvedFrom || appliedFilters.resolvedTo)}
                    onFromChange={setResolvedFromFilter}
                    onToChange={setResolvedToFilter}
                  />
                  <StyledButton variant="contained" onClick={applyFilters} sx={{ minWidth: 120 }}>
                    Apply
                  </StyledButton>
                  <StyledButton variant="outlined" onClick={resetFilters} sx={{ minWidth: 120 }}>
                    Reset
                  </StyledButton>
                </Stack>
              </Stack>
            </Paper>

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
              <Paper
                sx={{
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}>
                {isLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 0 }}>
                    <CircularProgress size={28} />
                  </Stack>
                ) : (
                  <>
                    <TableContainer
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        borderRadius: 1,
                        maxHeight: { xs: '46vh', md: 'none' },
                        '@media (max-height: 860px)': {
                          maxHeight: '42vh'
                        }
                      }}>
                      <Table
                        stickyHeader
                        size="small"
                        sx={{
                          width: '100%',
                          tableLayout: 'fixed',
                          '& .MuiTableCell-head': {
                            fontWeight: 700,
                            backgroundColor: 'grey.100',
                            fontSize: '0.78rem',
                            py: 0.6
                          },
                          '& .MuiTableCell-root': {
                            py: 1.05,
                            px: 0.7,
                            fontSize: '0.78rem',
                            verticalAlign: 'top',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word'
                          }
                        }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '5%' }}>Created</TableCell>
                            <TableCell sx={{ width: '6%' }}>QM #</TableCell>
                            <TableCell sx={{ width: '6%' }}>Status</TableCell>
                            <TableCell sx={{ width: '6%' }}>Type</TableCell>
                            <TableCell sx={{ width: '9%' }}>Reason</TableCell>
                            <TableCell sx={{ width: '5%' }}>PO</TableCell>
                            <TableCell sx={{ width: '12%' }}>Description</TableCell>
                            <TableCell sx={{ width: '8%' }}>Supplier</TableCell>
                            <TableCell sx={{ width: '7%' }}>Location</TableCell>
                            <TableCell sx={{ width: '8%' }}>Action</TableCell>
                            <TableCell sx={{ width: '9%' }}>Additional Details</TableCell>
                            <TableCell sx={{ width: '9%' }}>Raised By</TableCell>
                            <TableCell sx={{ width: '6%' }}>Closure</TableCell>
                            <TableCell sx={{ width: '4%' }}>Resolved</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.length > 0 ? (
                            rows.map((qm) => (
                              <TableRow
                                key={qm.qmId}
                                hover
                                sx={{ cursor: 'pointer' }}
                                selected={selectedQm?.qmId === qm.qmId}
                                onClick={() => openQm(qm)}>
                                <TableCell>{dayjs(qm.createdAt).format('DD/MM/YY')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{qm.qmNumber}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={formatStatusLabel(qm.status)}
                                    sx={getStatusChipSx(qm.status)}
                                  />
                                </TableCell>
                                <TableCell>{getQmTypeLabel(qm.type, qm.typeLabel)}</TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'normal' }}>
                                    {qm.reasonLabel || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{qm.poNumber || '-'}</TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: '0.78rem',
                                      lineHeight: 1.35,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}>
                                    {qm.description || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'normal' }}>
                                    {qm.supplierName || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'normal' }}>
                                    {getQmLocationLabel(qm)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ minWidth: 0 }}>
                                  {renderQmActionCellContent(qm.comments?.[0]?.body || qm.closureNotes)}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: '0.78rem',
                                      lineHeight: 1.35,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}>
                                    {qm.otherInformation || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'normal' }}>
                                    {qm.createdBy || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'normal' }}>
                                    {qm.closureLabel || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{qm.closedAt ? dayjs(qm.closedAt).format('DD/MM/YY') : '-'}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={14} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                No QM records found for the current filters.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack direction="row" justifyContent="center" sx={{ py: 1, flexShrink: 0 }}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.25}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%', px: 1 }}>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', fontWeight: 600 }}>
                          Showing {pageRangeStart}-{pageRangeEnd} of {totalCount}
                        </Typography>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                          color="primary"
                        />
                      </Stack>
                    </Stack>
                  </>
                )}
              </Paper>
            </Box>
          </Stack>
        </Box>
      </Box>

      <QmDetailDialog open={isDetailDialogOpen} onClose={closeDetailDialog} model={detailDialogModel} />

      <Dialog
        open={isSiteRolesDialogOpen}
        onClose={() => setIsSiteRolesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }
        }}>
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}>
                Site Inbound QM Site Specific Coordinators
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                Sites where your user is assigned as a Site Inbound QM site specific coordinator.
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setIsSiteRolesDialogOpen(false)} aria-label="Close site roles">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack spacing={0.75}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary' }}>Sites</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {siteInboundCoordinatorSites.map((site) => (
                <Chip
                  key={`${site.id}-${site.label}`}
                  size="small"
                  label={site.display}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Stack>
          </Stack>

          <Stack spacing={0.75}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary' }}>
              Coordinator Reasons
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {siteInboundCoordinatorReasonLabels.map((reason) => (
                <Chip key={reason.code} size="small" label={reason.label} variant="outlined" />
              ))}
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <StyledButton variant="outlined" onClick={() => setIsSiteRolesDialogOpen(false)}>
              Close
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>

      <Dialog
        open={isReprintDialogOpen && !!selectedQm}
        onClose={() => setIsReprintDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }
        }}>
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}>Reprint Label</Typography>
          <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary' }}>
            Choose one label or print all {Number(selectedQm?.packageCount || 0)} labels for this QM.
          </Typography>

          <Stack direction="row" spacing={1}>
            <Paper
              onClick={() => setReprintMode('ALL')}
              sx={{
                flex: 1,
                p: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: reprintMode === 'ALL' ? 'primary.main' : 'divider',
                backgroundColor: reprintMode === 'ALL' ? 'primary.50' : 'background.paper'
              }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                All {Number(selectedQm?.packageCount || 0)} Labels
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Print full quantity</Typography>
            </Paper>

            <Paper
              onClick={() => setReprintMode('ONE')}
              sx={{
                flex: 1,
                p: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: reprintMode === 'ONE' ? 'primary.main' : 'divider',
                backgroundColor: reprintMode === 'ONE' ? 'primary.50' : 'background.paper'
              }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>One Label</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Choose label number</Typography>
            </Paper>
          </Stack>

          {reprintMode === 'ONE' ? (
            <TextField
              type="number"
              size="small"
              label={`Label Number (1-${Number(selectedQm?.packageCount || 1)})`}
              value={reprintLabelNumber}
              onChange={(event) => setReprintLabelNumber(event.target.value)}
              inputProps={{ min: 1, max: Number(selectedQm?.packageCount || 1) }}
            />
          ) : null}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <StyledButton variant="outlined" onClick={() => setIsReprintDialogOpen(false)}>
              Cancel
            </StyledButton>
            <StyledButton variant="contained" onClick={() => void confirmReprintLabels()} disabled={isReprintingLabels}>
              Print
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>

      <Dialog
        open={!!selectedPreview}
        onClose={() => setSelectedPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.default'
          }
        }}>
        <Stack spacing={1} sx={{ p: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" noWrap title={selectedPreview?.name || ''}>
              {selectedPreview?.name || ''}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={() => setSelectedPreview(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          {selectedPreview ? (
            <Box
              component="img"
              src={selectedPreview.url}
              alt={selectedPreview.name}
              sx={{
                width: '100%',
                maxHeight: { xs: '70vh', md: '78vh' },
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          ) : null}
        </Stack>
      </Dialog>
    </Box>
  );
}
